import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TopNavigation from '../components/TopNavigation';
import { auth } from '../firebase';
import ImageWithFallback from '../components/ImageWithFallback';
import { uploadModeratedMedia } from '../utils/moderatedUpload';
import { getSeoConfig, getUserMe, logAdminAudit, queueSitemapJob as queueSitemapJobApi, setSeoConfig } from '../utils/secureFunctions';

type PageKey = 'landing' | 'about' | 'help' | 'privacy';

interface PageMeta {
  title: string;
  description: string;
  keywords: string;
}

interface SEOConfig {
  defaultTitle: string;
  defaultDescription: string;
  defaultKeywords: string;
  canonicalUrl: string;
  ogTitle: string;
  ogDescription: string;
  ogImageUrl?: string;
  twitterHandle: string;
  robotsAllowIndexing: boolean;
  sitemapEnabled: boolean;
  customHeadMarkup: string;
  trackingScripts: string;
  pages: Record<PageKey, PageMeta>;
}

const PAGE_LABELS: Record<PageKey, string> = {
  landing: 'Landing Page',
  about: 'About Page',
  help: 'Help Center',
  privacy: 'Privacy Policy',
};

const DEFAULT_PAGE_META: PageMeta = {
  title: 'Belivah — Inspire. Entertain. Connect.',
  description: 'Belivah is the uplifting social home for faith-forward creators, ministries, and communities.',
  keywords: 'Belivah, faith, community, christian creators, testimonies, worship, live',
};

const DEFAULT_SEO: SEOConfig = {
  defaultTitle: 'Belivah',
  defaultDescription: 'Belivah brings together testimonies, worship, music, and community in one modern hub.',
  defaultKeywords: 'Belivah, faith, testimonies, worship, christian community',
  canonicalUrl: 'https://www.belivah.com/',
  ogTitle: 'Belivah — Inspire. Entertain. Connect.',
  ogDescription: 'Join creators, ministries, and believers sharing testimonies, music, and live worship.',
  ogImageUrl: '',
  twitterHandle: '@belivahapp',
  robotsAllowIndexing: true,
  sitemapEnabled: true,
  customHeadMarkup: '<!-- custom head -->',
  trackingScripts: '<!-- analytics scripts -->',
  pages: {
    landing: { ...DEFAULT_PAGE_META },
    about: { ...DEFAULT_PAGE_META, title: 'About Belivah', description: 'Learn how Belivah empowers believers to create, share, and grow.' },
    help: { ...DEFAULT_PAGE_META, title: 'Belivah Support', description: 'Get help with live streaming, groups, testimonies, and more.' },
    privacy: { ...DEFAULT_PAGE_META, title: 'Belivah Privacy Policy', description: 'Understand how Belivah protects your data and privacy.' },
  },
};

const normalizePages = (pages?: Partial<Record<PageKey, PageMeta>>): Record<PageKey, PageMeta> => ({
  landing: { ...DEFAULT_PAGE_META, ...(pages?.landing || {}) },
  about: { ...DEFAULT_PAGE_META, ...(pages?.about || {}) },
  help: { ...DEFAULT_PAGE_META, ...(pages?.help || {}) },
  privacy: { ...DEFAULT_PAGE_META, ...(pages?.privacy || {}) },
});

const AdminSEO: React.FC = () => {
  const navigate = useNavigate();
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<SEOConfig>(DEFAULT_SEO);
  const [activePage, setActivePage] = useState<PageKey>('landing');
  const [status, setStatus] = useState('');
  const [queueMessage, setQueueMessage] = useState('');
  const [previewDoc, setPreviewDoc] = useState<'draft' | 'published'>('draft');

  useEffect(() => {
    const bootstrap = async () => {
      if (!auth.currentUser) { navigate('/login'); return; }
      try {
        const me = await getUserMe();
        const role = me.profile?.role || 'user';
        const allowed = role === 'admin' || role === 'super_admin' || role === 'moderator';
        setIsAuthorized(allowed);
        if (!allowed) { navigate('/home'); return; }
        await loadConfig();
      } catch {
        navigate('/home');
      } finally {
        setLoading(false);
      }
    };

    bootstrap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadConfig = async () => {
    try {
      setStatus('Loading config…');
      const [draftRes, publishedRes] = await Promise.all([
        getSeoConfig('draft'),
        getSeoConfig('published'),
      ]);
      if (draftRes.config) {
        hydrateConfig(draftRes.config);
        setPreviewDoc('draft');
      } else if (publishedRes.config) {
        hydrateConfig(publishedRes.config);
        setPreviewDoc('published');
      } else {
        setConfig(DEFAULT_SEO);
      }
      setStatus('');
    } catch (err: any) {
      setStatus(err?.message || 'Failed to load SEO config');
    }
  };

  const hydrateConfig = (data: any) => {
    setConfig(prev => ({
      ...prev,
      ...data,
      pages: normalizePages(data?.pages),
    }));
  };

  const updateField = <K extends keyof SEOConfig>(field: K, value: SEOConfig[K]) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const updatePageMeta = (page: PageKey, field: keyof PageMeta, value: string) => {
    setConfig(prev => ({
      ...prev,
      pages: {
        ...prev.pages,
        [page]: { ...prev.pages[page], [field]: value },
      },
    }));
  };

  const uploadOgImage = async (file: File) => {
    if (!auth.currentUser) return;
    setSaving(true);
    try {
      const ext = file.name.split('.').pop() || 'png';
      const filename = `${auth.currentUser.uid}_${Date.now()}.${ext}`;
      const upload = await uploadModeratedMedia({
        folder: 'seo',
        field: 'ogImageUrl',
        file,
        filename,
        contentType: file.type || 'image/png',
        metadata: { source: 'admin-seo', kind: 'og' },
      });
      updateField('ogImageUrl', upload.objectKey);
      setStatus('OG image uploaded');
    } catch (err: any) {
      setStatus(err?.message || 'Failed to upload image');
    } finally {
      setSaving(false);
    }
  };

  const persistConfig = async (target: 'draft' | 'published') => {
    if (!auth.currentUser) return;
    setSaving(true);
    try {
      await setSeoConfig(target, {
        ...config,
        pages: { ...config.pages },
      });
      await logAdminAudit({ action: target === 'draft' ? 'seo_save_draft' : 'seo_publish', payload: { fields: Object.keys(config) } });
      setPreviewDoc(target);
      setStatus(target === 'draft' ? 'Draft saved' : 'SEO published');
    } catch (err: any) {
      setStatus(err?.message || 'Failed to persist SEO config');
    } finally {
      setSaving(false);
    }
  };

  const queueSitemapJob = async () => {
    try {
      setQueueMessage('Queuing sitemap job…');
      await queueSitemapJobApi();
      setQueueMessage('Sitemap generation queued');
    } catch (err: any) {
      setQueueMessage(err?.message || 'Failed to queue job');
    }
  };

  const previewJson = useMemo(() => JSON.stringify(config, null, 2), [config]);

  if (loading) return <div style={styles.loading}>Loading SEO console…</div>;
  if (!isAuthorized) return null;

  const activeMeta = config.pages[activePage];

  return (
    <div style={styles.page}>
      <TopNavigation />
      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}>SEO Customizer</h1>
          <p style={styles.subtitle}>Control global metadata, sharing cards, and per-page overrides for Belivah surfaces.</p>
        </div>

        <div style={styles.actions}>
          <button style={styles.primaryBtn} disabled={saving} onClick={() => persistConfig('draft')}>Save Draft</button>
          <button style={styles.publishBtn} disabled={saving} onClick={() => persistConfig('published')}>Publish SEO</button>
          <button style={styles.linkBtn} disabled={saving} onClick={loadConfig}>Reload</button>
          <button style={styles.secondaryBtn} onClick={queueSitemapJob}>Queue Sitemap</button>
          <span style={styles.status}>{status}</span>
        </div>

        <div style={styles.grid}>
          <section style={styles.card}>
            <h3 style={styles.cardTitle}>Global Meta</h3>
            <label style={styles.label}>Default Title</label>
            <input style={styles.input} value={config.defaultTitle} onChange={e => updateField('defaultTitle', e.target.value)} />
            <label style={styles.label}>Default Description</label>
            <textarea style={styles.textarea} value={config.defaultDescription} onChange={e => updateField('defaultDescription', e.target.value)} />
            <label style={styles.label}>Default Keywords</label>
            <textarea style={styles.textarea} value={config.defaultKeywords} onChange={e => updateField('defaultKeywords', e.target.value)} />
            <label style={styles.label}>Canonical URL</label>
            <input style={styles.input} value={config.canonicalUrl} onChange={e => updateField('canonicalUrl', e.target.value)} />
          </section>

          <section style={styles.card}>
            <h3 style={styles.cardTitle}>Social / Open Graph</h3>
            <label style={styles.label}>OG Title</label>
            <input style={styles.input} value={config.ogTitle} onChange={e => updateField('ogTitle', e.target.value)} />
            <label style={styles.label}>OG Description</label>
            <textarea style={styles.textarea} value={config.ogDescription} onChange={e => updateField('ogDescription', e.target.value)} />
            <label style={styles.label}>Twitter Handle</label>
            <input style={styles.input} value={config.twitterHandle} onChange={e => updateField('twitterHandle', e.target.value)} />
            <label style={styles.label}>OG / Social Image</label>
            <div style={styles.ogRow}>
              <input type="file" accept="image/*" onChange={e => { const file = e.target.files?.[0]; if (file) uploadOgImage(file); }} />
              {config.ogImageUrl && (
                <ImageWithFallback src={config.ogImageUrl} alt="og" style={styles.ogPreview} />
              )}
            </div>
          </section>

          <section style={styles.card}>
            <h3 style={styles.cardTitle}>Indexing & Tracking</h3>
            <label style={styles.switchLabel}>
              <input type="checkbox" checked={config.robotsAllowIndexing} onChange={e => updateField('robotsAllowIndexing', e.target.checked)} />
              Allow indexing (robots.txt)
            </label>
            <label style={styles.switchLabel}>
              <input type="checkbox" checked={config.sitemapEnabled} onChange={e => updateField('sitemapEnabled', e.target.checked)} />
              Enable sitemap.xml
            </label>
            <label style={styles.label}>Custom &lt;head&gt; Markup</label>
            <textarea style={styles.codeArea} value={config.customHeadMarkup} onChange={e => updateField('customHeadMarkup', e.target.value)} />
            <label style={styles.label}>Tracking / Analytics Scripts</label>
            <textarea style={styles.codeArea} value={config.trackingScripts} onChange={e => updateField('trackingScripts', e.target.value)} />
          </section>

          <section style={styles.card}>
            <h3 style={styles.cardTitle}>Preview ({previewDoc})</h3>
            <pre style={styles.preview}>{previewJson}</pre>
            {queueMessage && <div style={styles.queueNote}>{queueMessage}</div>}
          </section>
        </div>

        <section style={styles.cardWide}>
          <div style={styles.cardHeader}>
            <div>
              <h3 style={styles.cardTitle}>Per-Page Overrides</h3>
              <p style={styles.cardSubtitle}>Fine tune metadata for each studio surface.</p>
            </div>
            <div style={styles.tabRow}>
              {(Object.keys(PAGE_LABELS) as PageKey[]).map((page) => (
                <button
                  key={page}
                  style={page === activePage ? styles.tabActive : styles.tab}
                  onClick={() => setActivePage(page)}
                >
                  {PAGE_LABELS[page]}
                </button>
              ))}
            </div>
          </div>
          <label style={styles.label}>Title</label>
          <input style={styles.input} value={activeMeta.title} onChange={e => updatePageMeta(activePage, 'title', e.target.value)} />
          <label style={styles.label}>Description</label>
          <textarea style={styles.textarea} value={activeMeta.description} onChange={e => updatePageMeta(activePage, 'description', e.target.value)} />
          <label style={styles.label}>Keywords</label>
          <textarea style={styles.textarea} value={activeMeta.keywords} onChange={e => updatePageMeta(activePage, 'keywords', e.target.value)} />
        </section>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: { minHeight: '100vh', background: '#F5EDE1' },
  loading: { padding: 32 },
  container: { maxWidth: 1200, margin: '0 auto', padding: 24, paddingTop: 80 },
  header: { marginBottom: 16 },
  title: { margin: 0, fontSize: 32, color: '#072B2C' },
  subtitle: { margin: '6px 0 0', color: '#4A5C5C' },
  actions: { display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16 },
  status: { alignSelf: 'center', color: '#4A5C5C', minWidth: 140 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginBottom: 24 },
  card: { background: '#FFFFFF', borderRadius: 16, padding: 18, boxShadow: '0 10px 30px rgba(7, 43, 44, 0.09)', display: 'flex', flexDirection: 'column', gap: 8 },
  cardTitle: { margin: '0 0 8px', color: '#072B2C' },
  cardSubtitle: { margin: 0, color: '#6C7B7C', fontSize: 14 },
  cardWide: { background: '#FFFFFF', borderRadius: 16, padding: 18, boxShadow: '0 15px 40px rgba(7,43,44,0.08)', marginBottom: 32 },
  cardHeader: { display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 },
  label: { fontSize: 13, fontWeight: 600, color: '#4A5C5C', marginTop: 8 },
  input: { borderRadius: 10, border: '1px solid #D8D8D8', padding: '10px 12px', fontSize: 14 },
  textarea: { borderRadius: 10, border: '1px solid #D8D8D8', padding: '10px 12px', minHeight: 70, fontSize: 14 },
  codeArea: { borderRadius: 10, border: '1px solid #D8D8D8', padding: '10px 12px', minHeight: 90, fontFamily: 'Consolas, monospace', fontSize: 13 },
  primaryBtn: { background: '#0B5D5E', color: '#fff', padding: '10px 18px', borderRadius: 10, border: 'none', cursor: 'pointer' },
  publishBtn: { background: '#C35B16', color: '#fff', padding: '10px 18px', borderRadius: 10, border: 'none', cursor: 'pointer' },
  secondaryBtn: { background: '#F0DCC0', color: '#8A5A2C', padding: '10px 18px', borderRadius: 10, border: 'none', cursor: 'pointer' },
  linkBtn: { background: 'transparent', color: '#0B5D5E', border: '1px solid #0B5D5E', padding: '10px 18px', borderRadius: 10, cursor: 'pointer' },
  switchLabel: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#4A5C5C' },
  ogRow: { display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' },
  ogPreview: { width: 160, height: 90, borderRadius: 10, objectFit: 'cover', border: '1px solid #E1E1E1' },
  preview: { background: '#0B1413', color: '#F1F5F4', padding: 12, borderRadius: 12, fontSize: 12, maxHeight: 280, overflow: 'auto' },
  queueNote: { marginTop: 12, fontSize: 13, color: '#0B5D5E' },
  tabRow: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  tab: { borderRadius: 999, border: '1px solid #D3C6B3', padding: '6px 14px', background: 'transparent', cursor: 'pointer', color: '#7A6C57' },
  tabActive: { borderRadius: 999, border: '1px solid #0B5D5E', padding: '6px 14px', background: '#0B5D5E', color: '#fff', cursor: 'pointer' },
};

export default AdminSEO;
